import { BALANCES_V2_BLOTTER_PREFIX } from '@src/containers/Blotters/BalancesV2/tokens';
import { CREDIT_BLOTTER_PREFIX } from '@src/containers/Blotters/Credit/tokens';
import { ORDERS_BLOTTER_PREFIX } from '@src/containers/Blotters/Orders/tokens';
import { POSITIONS_V3_BLOTTER_PREFIX } from '@src/containers/Blotters/PositionsV3/tokens';
import { RECON_MISMATCHES_BLOTTER_PREFIX } from '@src/containers/Blotters/Reconciliation/tokens';
import { TRADES_BLOTTER_PREFIX } from '@src/containers/Blotters/Trades/tokens';
import { TRANSFERS_BLOTTER_PREFIX } from '@src/containers/Blotters/Transfers/tokens';
import { MONITORING_BLOTTER_PREFIX } from '@src/containers/Monitoring/Blotters/types';
import { PERFORMANCE_BLOTTER_PREFIX, PERFORMANCE_BLOTTER_TABS_ID } from '@src/containers/Portfolio/Performance/tokens';
import {
  TREASURY_BLOTTER_PREFIX,
  TREASURY_MANAGEMENT_BLOTTER_TABS_ID,
} from '@src/containers/Portfolio/TreasuryManagement/tokens';
import {
  BLOTTER_ID_ALLOCATIONS_ORDER_DETAILS,
  BLOTTER_ID_CUSTOMER_TRADES_ORDER_DETAILS,
  BLOTTER_ID_EXEC_REPORTS_ORDER_DETAILS,
  BLOTTER_ID_MARKET_EXEC_REPORTS_ORDER_DETAILS,
  BLOTTER_ID_MARKET_ORDERS_ORDER_DETAILS,
  BLOTTER_ID_TRADES_ORDER_DETAILS,
} from '@src/tokens/blotters';
import { get } from 'lodash-es';
import { validate as uuidValidate, version as uuidVersion } from 'uuid';
import { TRADE_SETTLEMENT_BLOTTER_PREFIX } from '../../../containers/Dealer/CustomerSettlement/CustomerSettlementMonitoring/tokens';
import { VALID_SECTIONS } from '../../../types/LayoutConfig';
import type { AppConfigStateWithLegacyLayoutConfigs } from './legacyLayoutConfigs.types';

interface PruningConfig {
  blotterTabPrefix: string;
  tabPrefix: string;
}

// For each blotter + tab combo we want to prune, we need a config supplied so we know where to look
const PRUNING_CONFIGS: PruningConfig[] = [
  {
    blotterTabPrefix: CREDIT_BLOTTER_PREFIX,
    tabPrefix: CREDIT_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: ORDERS_BLOTTER_PREFIX,
    tabPrefix: ORDERS_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: BALANCES_V2_BLOTTER_PREFIX,
    tabPrefix: BALANCES_V2_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: POSITIONS_V3_BLOTTER_PREFIX,
    tabPrefix: POSITIONS_V3_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: TRADES_BLOTTER_PREFIX,
    tabPrefix: TRADES_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: TRANSFERS_BLOTTER_PREFIX,
    tabPrefix: TRANSFERS_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: RECON_MISMATCHES_BLOTTER_PREFIX,
    tabPrefix: RECON_MISMATCHES_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: PERFORMANCE_BLOTTER_PREFIX,
    tabPrefix: PERFORMANCE_BLOTTER_TABS_ID,
  },
  {
    blotterTabPrefix: TREASURY_BLOTTER_PREFIX,
    tabPrefix: TREASURY_MANAGEMENT_BLOTTER_TABS_ID,
  },
  {
    blotterTabPrefix: MONITORING_BLOTTER_PREFIX,
    tabPrefix: MONITORING_BLOTTER_PREFIX,
  },
  {
    blotterTabPrefix: TRADE_SETTLEMENT_BLOTTER_PREFIX,
    tabPrefix: TRADE_SETTLEMENT_BLOTTER_PREFIX,
  },
];

const ORDER_DETAILS_CONFIGS: string[] = [
  BLOTTER_ID_MARKET_ORDERS_ORDER_DETAILS,
  BLOTTER_ID_TRADES_ORDER_DETAILS,
  BLOTTER_ID_CUSTOMER_TRADES_ORDER_DETAILS,
  BLOTTER_ID_EXEC_REPORTS_ORDER_DETAILS,
  BLOTTER_ID_MARKET_EXEC_REPORTS_ORDER_DETAILS,
  BLOTTER_ID_ALLOCATIONS_ORDER_DETAILS,
];

/**
 * This function prunes PersistedBlotterTable state belonging to no longer existent Order Details Market Tabs.
 *
 * Given an array of configs, we go through all of the Market tabs, specifically checking for OrderDetails
 * Order details tab contains orderID property, which means that if we cannot find it means it has been closed and we can safely prune it
 */
export function pruneOrderDetailsBlotterState(config: any) {
  const allBlotterTabs: object | undefined = get(config, 'blotters3');
  if (!allBlotterTabs) {
    return;
  }

  for (const blotterTabPrefix of ORDER_DETAILS_CONFIGS) {
    const blotterTabsForPrefixEntries = [
      ...Object.entries(allBlotterTabs).filter(([key]) => key.startsWith(blotterTabPrefix)),
    ];

    const marketTabs = get(config, `marketTabs`) || [];
    const orderDetailsTabs = marketTabs.filter((tab: any) => tab.orderID != null);

    for (const [key] of blotterTabsForPrefixEntries) {
      const splitString = key.split('/');
      const orderID = splitString[1];

      if (!orderID) {
        continue;
      }

      if (orderDetailsTabs.some((tab: any) => tab.orderID === orderID)) {
        // We exist! Do not prune.
        continue;
      }

      // Else we do not exist and we should delete ourselves
      delete allBlotterTabs[key as keyof typeof allBlotterTabs];
    }
  }
}

/**
 * This function prunes PersistedBlotterTable state belonging to no longer existent tabs.
 *
 * Given an array of configs which define how blotter prefixes are related to tab prefixes (IDs), we go through all of these
 * pieces of blotter state and check if their equivalent tab is still present in the tabs state. If it is not, then remove
 * the persisted blotter state since it can never get reached again.
 */
export function prunePersistedBlotterTableState(config: any) {
  const allBlotterTabs: object | undefined = get(config, 'blotters3');
  if (!allBlotterTabs) {
    return;
  }

  for (const { blotterTabPrefix, tabPrefix } of PRUNING_CONFIGS) {
    const blotterTabsForPrefixEntries = [
      ...Object.entries(allBlotterTabs).filter(([key]) => key.startsWith(blotterTabPrefix)),
    ];

    for (const [key] of blotterTabsForPrefixEntries) {
      const splitString = key.split('/');
      const uuidIndex = splitString.findIndex(str => isUuid(str));

      // We only prune on blotterTab keys that contain a uuid. These have been generated by the user creating new tabs themselves.
      if (uuidIndex === -1) {
        continue;
      }

      // See if we can find ourselves in the tabs.<tabPath>.find(tab => tab.id === uuid)
      const tabsItems = get(config, `tabs.${tabPrefix}.items`); // try to grab the items array

      const uuid = splitString[uuidIndex];
      if (tabsItems != null && tabsItems instanceof Array && tabsItems.some(item => item.id === uuid)) {
        // We exist! Do not prune.
        continue;
      }

      if (tabIdExistsInLayoutPanelParams(config, uuid)) {
        // Tab is still referenced from a dockview panel (e.g. flexible layout); keep blotter table state.
        continue;
      }

      // Else we do not exist and we should delete ourselves
      delete allBlotterTabs[key as keyof typeof allBlotterTabs];
    }
  }
}

const supportedUuidVersions = [1, 4];
function isUuid(str: string): boolean {
  return uuidValidate(str) && supportedUuidVersions.some(v => uuidVersion(str) === v);
}

function panelParamsListsTabId(panel: unknown, tabId: string): boolean {
  if (panel == null || typeof panel !== 'object') {
    return false;
  }
  const items = (panel as { params?: { tabs?: { items?: unknown[] } } }).params?.tabs?.items;
  return (
    Array.isArray(items) &&
    items.some(item => typeof item === 'object' && item != null && (item as { id?: string }).id === tabId)
  );
}

function layoutSpecListsTabIdInPanels(layoutConfig: unknown, tabId: string): boolean {
  if (layoutConfig == null || typeof layoutConfig !== 'object') {
    return false;
  }
  const panels = (layoutConfig as { dockViewLayout?: { panels?: Record<string, unknown> } }).dockViewLayout?.panels;
  if (panels == null) {
    return false;
  }
  for (const panel of Object.values(panels)) {
    if (panelParamsListsTabId(panel, tabId)) {
      return true;
    }
  }
  return false;
}

/**
 * Returns true if any dockview panel in persisted layout configs lists this tab id in `params.tabs.items`.
 * Tabs can live only in panel params when using flexible layout, without a matching `tabs.<id>` entry.
 */
function tabIdExistsInLayoutPanelParams(config: any, tabId: string): boolean {
  const spacesConfigs = config.spacesConfigs as AppConfigStateWithLegacyLayoutConfigs['spacesConfigs'] | undefined;
  if (spacesConfigs != null && typeof spacesConfigs === 'object') {
    for (const section of VALID_SECTIONS) {
      const sectionConfig = spacesConfigs[section];
      if (sectionConfig == null || typeof sectionConfig !== 'object') {
        continue;
      }
      const layouts = sectionConfig.layouts;
      if (!Array.isArray(layouts)) {
        continue;
      }
      for (const layoutConfig of layouts) {
        if (layoutSpecListsTabIdInPanels(layoutConfig, tabId)) {
          return true;
        }
      }
    }
  }

  const layoutConfigs = config.layoutConfigs as AppConfigStateWithLegacyLayoutConfigs['layoutConfigs'];
  if (layoutConfigs == null || typeof layoutConfigs !== 'object') {
    return false;
  }

  for (const section of Object.values(layoutConfigs)) {
    if (section == null || typeof section !== 'object') {
      continue;
    }
    for (const layoutConfig of Object.values(section)) {
      if (layoutSpecListsTabIdInPanels(layoutConfig, tabId)) {
        return true;
      }
    }
  }

  return false;
}
